import BotoesSalvarExcluir from "@/shared/components/forms/BotoesSalvarExcluir";
import Carregando from "@/shared/components/Carregando";
import ModalExcluir from "@/shared/components/modals/ModalExcluir";
import ModalHistorico from "@/modules/system/historico/ModalHistorico";
import { OperationalProfilesSection } from "../forms/profiles/operational-profiles-section";
import {
  buildPerfisOperacionaisFromForm,
  resolveActiveOperationalProfileKind,
  OPERATIONAL_PROFILE_LABELS,
  syncPerfisOperacionaisToPayload,
} from "../forms/profiles/operational-profiles.utils";
import { CreatePersonDto, UpdatePersonDto } from "@/api.types";
import { DataIso } from "@/shared/utils/datas";
import { EditadoPorRodape } from "@/shared/components/EditadoPorRodape";
import { buildPersonFormSchema } from "@/modules/people/presentation/person.schema";
import { normalizePersonFormFromApi, PersonFormDto, shouldIncludeProfileInPayload } from "@/modules/people/presentation/person-form.utils";
import { FormProvider, useForm, type FieldErrors, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PermissionEnum } from "@/modules/system/auth/domain/enums/permission.enum";
import { RoleEnum } from "@/modules/system/auth/domain/enums/role.enum";
import { TabDadosPessoais } from "../forms/form.dadospessoais";
import { Tabelas } from "@/shared/constants/tables";
import { UsuarioProfile } from "../forms/profiles/usuario-profile";
import { ValidarPermissaoUsuario } from "@/modules/system/auth/application/validate-permissions";
import { format } from "date-fns";
import { readPerson, updatePerson, createPerson, deletePerson } from "../../person.service";
import { toast } from "react-toastify";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import Links from '@/shared/constants/navegacao';
import { useNavigate, useParams } from "react-router-dom";
import { useBreadcrumbActions } from "@/shared/contexts/BreadcrumbActionsContext";
import { validaCPF } from "@/shared/utils/cpfs";
import MyContext from "@/shared/contexts/AppContext";
import { NavigationView } from "@/shared/constants/navigation-view.enum";
import { isUsuarioAdministrador, isVisaoExplicitaUsuario } from "@/shared/constants/visao-sistema.constants";

type PostSaveAction = 'list' | 'continue' | 'addAnother';

const PAGE_TIPO = 'todos' as const;

export default function PersonEditPage() {
  const { id } = useParams();
  const key = id ?? String(Date.now());
  return <PersonEditPageCore key={key} />;
}

function PersonEditPageCore() {
  const navigate = useNavigate();
  const params = useParams();
  const id = params.id !== undefined ? String(params.id) : null;
  const btnSalvar = id ? 'Salvar' : 'Cadastrar';
  const { setActions } = useBreadcrumbActions();

  const podeVerDefensor = ValidarPermissaoUsuario(RoleEnum.DEFENSOR, PermissionEnum.UPDATE)
  const podeVerEstagiario = ValidarPermissaoUsuario(RoleEnum.ESTAGIARIO, PermissionEnum.UPDATE)
  const podeVerColaborador = ValidarPermissaoUsuario(RoleEnum.COLABORADOR, PermissionEnum.UPDATE)
  const podeVerUsuarios = ValidarPermissaoUsuario(RoleEnum.USUARIO, PermissionEnum.UPDATE)
  const podeGerenciarPerfilOperacional =
    podeVerDefensor || podeVerEstagiario || podeVerColaborador;
  const { authVisao } = useContext(MyContext)!;
  const podeGerenciarAdministrador = authVisao === NavigationView.Administrador;
  const podeEditarVisaoUsuario = podeGerenciarAdministrador;

  const [isExecuting, setIsExecuting] = useState(false);
  const [modaExcluirShow, setModalExcluirShow] = useState(false);
  const [modalHistorico, setModalHistorico] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loadedLists] = useState(true);
  const [pessoaEhAdministrador, setPessoaEhAdministrador] = useState(false);

  const validationContext = useMemo(
    () => ({ tipo: PAGE_TIPO, podeVerUsuarios, podeVerColaborador }),
    [podeVerUsuarios, podeVerColaborador],
  );

  const personFormSchema = useMemo(
    () => buildPersonFormSchema(validationContext),
    [validationContext],
  );

  const methods = useForm<PersonFormDto>({
    shouldUnregister: false,
    resolver: zodResolver(personFormSchema as never) as Resolver<PersonFormDto>,
    mode: 'onTouched',
    reValidateMode: 'onChange',
    shouldFocusError: false,
  });
  const { getValues } = methods;
  const usuarioForm = methods.watch('usuario');
  methods.watch('perfisOperacionais');
  const cadastroAdministradorBloqueado =
    (pessoaEhAdministrador || isUsuarioAdministrador(usuarioForm)) && !podeGerenciarAdministrador;

  const indexLink = Links.Pessoa_Index;
  const createLink = Links.Pessoa_Criar;

  const postSaveActionRef = useRef<PostSaveAction | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  const carregarPessoa = useCallback(async () => {
    if (!id || !loadedLists) return;
    try {
      const res = await readPerson({
        skip: 0,
        take: 1,
        id,
        comTelefone: true,
        comEndereco: true,
        comUsuario: true,
        comDefensor: true,
        comEstagiario: true,
        comEstagios: true,
        comColaborador: true,
      });
      const pessoa = res[0]?.[0];

      if (pessoa) {
        setPessoaEhAdministrador(isUsuarioAdministrador(pessoa.usuario));

        if (pessoa.usuario) {
          pessoa.usuario.corfundotabela = pessoa.usuario.corfundotabela ?? '#e5e5e5';
          pessoa.usuario.corletratabela = pessoa.usuario.corletratabela ?? '#000000';
        }

        const formatDateInput = (value?: string | Date | null, pattern: string = "yyyy-MM-dd") =>
          value ? format(new Date(value), pattern) : "";

        const normalized = normalizePersonFormFromApi(pessoa);
        const pessoaFormatada: PersonFormDto = {
          ...normalized,
          cpf: pessoa.cpf ? validaCPF(pessoa.cpf, true) : pessoa.cpf,
          dataNascimento: pessoa.dataNascimento ? format(new Date(pessoa.dataNascimento), 'yyyy-MM-dd') : '',
          foto: pessoa.foto,
          defensoresHistorico: (pessoa as PersonFormDto).defensoresHistorico ?? [],
          estagiariosHistorico: (pessoa as PersonFormDto).estagiariosHistorico ?? [],
          colaboradoresHistorico: ((pessoa as PersonFormDto).colaboradoresHistorico ?? []).map((item) => ({
            ...item,
            data_cessao: item.data_cessao
              ? format(new Date(item.data_cessao), "yyyy-MM-dd")
              : "",
            data_saida: item.data_saida
              ? format(new Date(item.data_saida), "yyyy-MM-dd")
              : "",
          })),
          colaborador: {
            ...normalized.colaborador,
            data_cessao: pessoa.colaborador?.data_cessao
              ? format(new Date(pessoa.colaborador.data_cessao), "yyyy-MM-dd")
              : "",
            data_saida: pessoa.colaborador?.data_saida
              ? format(new Date(pessoa.colaborador.data_saida), "yyyy-MM-dd")
              : "",
          },
          estagiario: {
            ...normalized.estagiario,
            estagios: normalized.estagiario?.estagios?.map((estagio) => ({
              ...estagio,
              data_inicio_estagio: formatDateInput(estagio.data_inicio_estagio),
              data_fim_estagio: formatDateInput(estagio.data_fim_estagio),
              desligamento: formatDateInput(estagio.desligamento),
              data_colacao_grau: formatDateInput(estagio.data_colacao_grau, 'yyyy-MM'),
            })) ?? [],
          },
        };
        pessoaFormatada.perfisOperacionais = buildPerfisOperacionaisFromForm(pessoaFormatada);
        pessoaFormatada.perfisOperacionaisRemovidos = [];
        methods.reset(pessoaFormatada);
      }
    } catch (error) {
      toast(String(error));
    } finally {
      setLoaded(true);
    }
  }, [id, loadedLists, methods]);

  useEffect(() => {
    if (!id) {
      methods.reset({ perfisOperacionais: [] } as unknown as PersonFormDto);
      setPessoaEhAdministrador(false);
      setLoaded(true);
      return;
    }
    if (!loadedLists) return;
    carregarPessoa();
  }, [id, loadedLists, carregarPessoa, methods]);

  const handleError = (fieldErrors: FieldErrors<PersonFormDto>) => {
    if (import.meta.env.DEV) {
      console.warn('[PersonEdit] Validação:', fieldErrors);
    }
    toast.error('Corrija os campos destacados antes de salvar.');
  };

  const handleSubmit = (data: PersonFormDto) => {
    if (cadastroAdministradorBloqueado) {
      toast.error('Somente um administrador pode gerenciar o cadastro de outro administrador.');
      return;
    }

    const action: PostSaveAction = postSaveActionRef.current ?? 'list';
    postSaveActionRef.current = null;

    // Ajusta manualmente alguns campos.
    let input = {
      ...data,
      cpf: data.cpf ? data.cpf.replace(/\D/g, '') : data.cpf
    } as PersonFormDto;

    delete input.defensoresHistorico;
    delete input.estagiariosHistorico;
    delete input.colaboradoresHistorico;

    syncPerfisOperacionaisToPayload(input);
    if (input.telefone?.numero)
      input.telefone.numero = input.telefone.numero.replace(/\D/g, '')

    if (input.celular?.numero)
      input.celular.numero = input.celular.numero.replace(/\D/g, '')

    if (!shouldIncludeProfileInPayload(input.usuario, podeVerUsuarios, true))
      delete input.usuario;
    else if (input.usuario) {
      delete (input.usuario as { username?: string }).username;
      if (!podeEditarVisaoUsuario) {
        delete (input.usuario as { visao?: number | null }).visao;
        const usuarioDirty = methods.formState.dirtyFields.usuario;
        const usuarioFoiAlterado =
          !!usuarioDirty && Object.keys(usuarioDirty as object).length > 0;
        if (input.usuario.id && !usuarioFoiAlterado) {
          delete input.usuario;
        }
      } else {
        const rawVisao = (input.usuario as { visao?: string | number | null }).visao;
        if (rawVisao === '' || rawVisao == null) {
          (input.usuario as { visao?: number | null }).visao = null;
        } else {
          (input.usuario as { visao?: number | null }).visao = Number(rawVisao);
        }
      }
    }

    const incluiPerfisOperacionais = true;

    if (!shouldIncludeProfileInPayload(input.estagiario, podeVerEstagiario, incluiPerfisOperacionais))
      delete input.estagiario;

    if (!shouldIncludeProfileInPayload(input.defensor, podeVerDefensor, incluiPerfisOperacionais))
      delete input.defensor;

    if (!shouldIncludeProfileInPayload(input.colaborador, podeVerColaborador, incluiPerfisOperacionais))
      delete input.colaborador;

    if (input.colaborador) {
      if (!input.colaborador.funcao?.id) {

        // @ts-expect-error null
        input.colaborador.funcao = null;
      }
    }

    if (input.estagiario) {
      input.estagiario.estagios = input.estagiario?.estagios?.map((item) => {
        return (
          {
            ...item,
            data_colacao_grau: DataIso(item.data_colacao_grau),
            data_inicio_estagio: DataIso(item.data_inicio_estagio),
            data_fim_estagio: DataIso(item.data_fim_estagio),
            desligamento: DataIso(item.desligamento),
          }
        )
      })
    }

    setIsExecuting(true);
    if (id) {
      updatePerson(input as UpdatePersonDto)
        .then(() => {
          toast.success(`Cadastro de ${input.nome} atualizado com sucesso.`);
          if (action === 'list') navigate(indexLink);
          else if (action === 'addAnother') {
            methods.reset({});
            navigate(createLink);
          }
          else if (action === 'continue') carregarPessoa();
        })
        .catch((res) => {
          toast.error(res instanceof Error ? res.message : String(res));
        }).finally(() => {
          setIsExecuting(false);
        });
    } else {
      createPerson(input as CreatePersonDto)
        .then((newId) => {
          toast.success(`Cadastro de ${input.nome} realizado com sucesso.`);
          if (action === 'list') navigate(indexLink);
          else if (action === 'addAnother') {
            methods.reset({});
            // Permanece na página de criar para adicionar outro.
          }
          else if (action === 'continue' && newId) {
            navigate(`${Links.Pessoa_Editar}/${newId}`);
          }
        })
        .catch((res) => {
          toast.error(res instanceof Error ? res.message : String(res));
        }).finally(() => {
          setIsExecuting(false);
        });
    }
  }

  const handleExcluir = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    if (cadastroAdministradorBloqueado) {
      toast.error('Somente um administrador pode gerenciar o cadastro de outro administrador.');
      return;
    }

    if (id) {
      setModalExcluirShow(false);
      deletePerson(id)
        .then((res) => {
          toast.success(String(res).concat(' pessoa removida com sucesso.'));
          navigate(`${indexLink}`);
        })
        .catch((res) => {
          toast.error(String(res));
        });
    }
  };

  // Registra os botões no Breadcrumb
  useEffect(() => {
    setActions({
      secondary: { label: 'Novo', onClick: () => { methods.reset({}); navigate(createLink); } },
      history: { label: 'Histórico', onClick: () => setModalHistorico(true) },
    });
    return () => setActions({});
  }, [setActions, navigate, createLink, methods]);

  if (!loaded)
    return <Carregando />

  const activeProfileKind = resolveActiveOperationalProfileKind(getValues() as PersonFormDto);
  const usuarioVisaoForm = usuarioForm?.visao;
  const usuarioComVisaoAutomatica =
    !!usuarioForm?.ativo &&
    !isVisaoExplicitaUsuario(
      usuarioVisaoForm === '' || usuarioVisaoForm == null
        ? null
        : Number(usuarioVisaoForm),
    );
  const usuarioAtivoSemPerfil = usuarioComVisaoAutomatica && !activeProfileKind;

  return (
    <>
      <div className="row">
        <div className="col-12" title="Preencha somente o necessário.">
          <h4>{methods.watch('nome')}
            <small>
              <span className="text-muted ">{getValues().usuario?.ativo ? ' (usuário)' : ''}</span>
              {activeProfileKind ? (
                <span className="text-muted ">
                  {` (${OPERATIONAL_PROFILE_LABELS[activeProfileKind].toLowerCase()})`}
                </span>
              ) : null}
            </small>
          </h4>
        </div>
      </div>
      <hr className="pb-2" />

      {cadastroAdministradorBloqueado && (
        <div className="alert alert-warning" role="alert">
          Somente um administrador pode gerenciar o cadastro de outro administrador. Esta tela está em modo de consulta.
        </div>
      )}

      {usuarioAtivoSemPerfil && !podeEditarVisaoUsuario && (
        <div className="alert alert-warning" role="alert">
          {podeGerenciarPerfilOperacional
            ? 'Este cadastro possui usuário ativo com visão automática, mas sem perfil operacional. Adicione um perfil (defensor, estagiário ou colaborador) abaixo para concluir o vínculo com o sistema.'
            : 'Este cadastro possui usuário ativo sem perfil operacional e visão automática. Peça a um administrador para definir a visão no sistema ou conceder permissão para incluir perfis.'}
        </div>
      )}

      <FormProvider {...methods}>
        <fieldset disabled={cadastroAdministradorBloqueado}>
          <form ref={formRef} onSubmit={methods.handleSubmit(handleSubmit, handleError)}>

            <TabDadosPessoais methods={methods} />

            <OperationalProfilesSection
              methods={methods}
              pageTipo={PAGE_TIPO}
              podeVerDefensor={podeVerDefensor}
              podeVerEstagiario={podeVerEstagiario}
              podeVerColaborador={podeVerColaborador}
            />

            {podeVerUsuarios && (
              <UsuarioProfile
                methods={methods}
                podeVerUsuarios={podeVerUsuarios}
                podeGerenciarAdministrador={podeGerenciarAdministrador}
                podeEditarVisaoUsuario={podeEditarVisaoUsuario}
              />
            )}

            <EditadoPorRodape dados={{ editadoPor: getValues("editadoPor"), editadoEm: getValues("editadoEm") }} />

            {!cadastroAdministradorBloqueado && (
              <BotoesSalvarExcluir
                isLoading={isExecuting}
                texto={btnSalvar}
                textoLoading=" Salvando..."
                id={id || undefined}
                onExcluirClick={() => setModalExcluirShow(true)}
                onSalvarEAdicionarOutros={() => {
                  postSaveActionRef.current = 'addAnother';
                  formRef.current?.requestSubmit();
                }}
                onSalvarEContinuar={() => {
                  postSaveActionRef.current = 'continue';
                  formRef.current?.requestSubmit();
                }}
              />
            )}
          </form>
        </fieldset>
      </FormProvider>

      {id && <>
        <ModalExcluir show={modaExcluirShow} handleExcluir={handleExcluir} handleHide={() => setModalExcluirShow(false)} />
        <ModalHistorico show={modalHistorico} handleHide={() => setModalHistorico(false)} tabela={Tabelas.pessoa} idRegistro={id} />
      </>
      }
    </>
  );
}